
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const Adviser = ({ data }) => {
  const [ownerPropertyCount, setOwnerPropertyCount] = useState(0);
  const [builderPropertyCount, setBuilderPropertyCount] = useState(0);

  const animateCount = (target = 0, setFn) => {
    let count = 0;
    const maxDisplay = target > 50 ? 50 : target;
    const duration = 1000; // Total animation duration in ms
    const stepTime = Math.max(Math.floor(duration / (maxDisplay || 1)), 20);

    const interval = setInterval(() => {
      count++;
      if (count >= maxDisplay) {
        clearInterval(interval);
        setFn(target > 50 ? "50+" : target);
      } else {
        setFn(count);
      }
    }, stepTime);
  };

  useEffect(() => {
    if (data && data.status === 1) {
      const {
        owner_property_count = 0,
        builder_property_count = 0,
      } = data;

      animateCount(owner_property_count, setOwnerPropertyCount);
      animateCount(builder_property_count, setBuilderPropertyCount);
    }
  }, [data]);

  return (
    <section className="bg-white py-10 w-full">
      <div className="w-full px-4 sm:px-6 lg:px-8 max-w-[1400px] mx-auto">
        {/* Section Header */}
        <div className="mb-8 text-left">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
            Select Category
          </h2>
          <p className="text-gray-500 mt-1.5 text-base sm:text-lg">
            Go from browsing to buying with trusted advisers
          </p>
        </div>

        {/* Categories Grid - 2 columns side-by-side on screen */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-8 w-full">
          {/* Adviser Card 1: Owner */}
          <Link
            to="/advisordashboard?label=Owner"
            className="group relative flex items-center justify-between bg-slate-50 border border-slate-200/80 rounded-2xl p-5 sm:p-6 no-underline transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-200/50 hover:bg-white hover:border-slate-300 w-full"
          >
            {/* Image Collage Grid */}
            <div className="w-5/12 sm:w-2/5 flex-shrink-0 grid grid-cols-2 gap-1.5 p-1.5 bg-slate-200/60 rounded-xl overflow-hidden">
              <div className="col-span-2 h-24 sm:h-28 overflow-hidden rounded-lg">
                <img
                  src="/image/commercial.jpg"
                  alt="Owner Property Primary"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
                />
              </div>
              <div className="h-14 sm:h-16 overflow-hidden rounded-md">
                <img
                  src="/image/commercial.jpg"
                  alt="Owner Property Secondary"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
                />
              </div>
              <div className="h-14 sm:h-16 overflow-hidden rounded-md">
                <img
                  src="/image/commercial.jpg"
                  alt="Owner Property Tertiary"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
                />
              </div>
            </div>

            {/* Content Area */}
            <div className="ml-6 flex-1 flex flex-col justify-between py-1">
              <div>
                <span className="text-3xl sm:text-5xl font-black text-rose-800 tracking-tight block">
                  {ownerPropertyCount}
                </span>
                <h3 className="text-lg sm:text-xl font-bold text-gray-800 mt-1.5 group-hover:text-rose-800 transition-colors">
                  Owner
                </h3>
              </div>

              <div className="mt-5 flex items-center text-sm font-semibold text-rose-800">
                <span>Explore Properties</span>
                <svg
                  className="w-4 h-4 ml-2 transform group-hover:translate-x-1.5 transition-transform duration-300 ease-out"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </div>
            </div>
          </Link>

          {/* Adviser Card 2: Builder */}
          <Link
            to="/advisordashboard?label=Builder"
            className="group relative flex items-center justify-between bg-slate-50 border border-slate-200/80 rounded-2xl p-5 sm:p-6 no-underline transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-200/50 hover:bg-white hover:border-slate-300 w-full"
          >
            {/* Image Collage Grid */}
            <div className="w-5/12 sm:w-2/5 flex-shrink-0 grid grid-cols-2 gap-1.5 p-1.5 bg-slate-200/60 rounded-xl overflow-hidden">
              <div className="col-span-2 h-24 sm:h-28 overflow-hidden rounded-lg">
                <img
                  src="/image/plots.jpg"
                  alt="Builder Property Primary"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
                />
              </div>
              <div className="h-14 sm:h-16 overflow-hidden rounded-md">
                <img
                  src="/image/plots.jpg"
                  alt="Builder Property Secondary"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
                />
              </div>
              <div className="h-14 sm:h-16 overflow-hidden rounded-md">
                <img
                  src="/image/plots.jpg"
                  alt="Builder Property Tertiary"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
                />
              </div>
            </div>

            {/* Content Area */}
            <div className="ml-6 flex-1 flex flex-col justify-between py-1">
              <div>
                <span className="text-3xl sm:text-5xl font-black text-rose-800 tracking-tight block">
                  {builderPropertyCount}
                </span>
                <h3 className="text-lg sm:text-xl font-bold text-gray-800 mt-1.5 group-hover:text-rose-800 transition-colors">
                  Builders
                </h3>
              </div>

              <div className="mt-5 flex items-center text-sm font-semibold text-rose-800">
                <span>Explore Projects</span>
                <svg
                  className="w-4 h-4 ml-2 transform group-hover:translate-x-1.5 transition-transform duration-300 ease-out"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </div>
            </div>
          </Link>
           {/* Adviser Card 2: Builder */}
          <Link
            to="/advisordashboard?label=Builder"
            className="group relative flex items-center justify-between bg-slate-50 border border-slate-200/80 rounded-2xl p-5 sm:p-6 no-underline transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-200/50 hover:bg-white hover:border-slate-300 w-full"
          >
            {/* Image Collage Grid */}
            <div className="w-5/12 sm:w-2/5 flex-shrink-0 grid grid-cols-2 gap-1.5 p-1.5 bg-slate-200/60 rounded-xl overflow-hidden">
              <div className="col-span-2 h-24 sm:h-28 overflow-hidden rounded-lg">
                <img
                  src="/image/plots.jpg"
                  alt="Builder Property Primary"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
                />
              </div>
              <div className="h-14 sm:h-16 overflow-hidden rounded-md">
                <img
                  src="/image/plots.jpg"
                  alt="Builder Property Secondary"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
                />
              </div>
              <div className="h-14 sm:h-16 overflow-hidden rounded-md">
                <img
                  src="/image/plots.jpg"
                  alt="Builder Property Tertiary"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
                />
              </div>
            </div>

            {/* Content Area */}
            <div className="ml-6 flex-1 flex flex-col justify-between py-1">
              <div>
                <span className="text-3xl sm:text-5xl font-black text-rose-800 tracking-tight block">
                  {builderPropertyCount}
                </span>
                <h3 className="text-lg sm:text-xl font-bold text-gray-800 mt-1.5 group-hover:text-rose-800 transition-colors">
                  Upcoming Projects
                </h3>
              </div>

              <div className="mt-5 flex items-center text-sm font-semibold text-rose-800">
                <span>Explore Projects</span>
                <svg
                  className="w-4 h-4 ml-2 transform group-hover:translate-x-1.5 transition-transform duration-300 ease-out"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </div>
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Adviser;